/* global GLOBAL_PROMO_COOKIE_KEY */

import { cookie } from 'reduxModules/utils/cookie'

// SPEC-082: the promo cookie must be readable on every page, /checkout in
// particular. Written without a path, the browser scoped it to the directory
// of the page that set it, so a promo link landing on /subscriptions/plans
// stored it at path=/subscriptions: a full load of /checkout (reload, new
// tab, iOS crash-reload, PayPal return) could not see it and the subscribe
// went out without the coupon.
const PROMO_COOKIE_OPTIONS = { path: '/' }
// Where those pre-SPEC-082 copies live. Returning visitors may still carry
// one; on /subscriptions pages the browser lists it before the root cookie,
// so it would shadow every newer value.
const LEGACY_PROMO_COOKIE_OPTIONS = { path: '/subscriptions' }

// SPEC-092: a code from a promo link outlives the browser session (an ad
// click today, the purchase tomorrow), up to the promotion's end and never
// more than 14 days. The expiry travels inside the value as `expiresAt`:
// document.cookie can't read an expiry back, and every read re-homes the
// cookie, which would otherwise demote it to a session cookie.
export const PROMO_COOKIE_MAX_AGE_MS = 14 * 24 * 60 * 60 * 1000

const expireLegacyPromoCookie = (dispatch) =>
  dispatch(cookie(GLOBAL_PROMO_COOKIE_KEY, null, LEGACY_PROMO_COOKIE_OPTIONS))

export const parsePromoCookie = (value) => {
  try {
    const parsed = value ? JSON.parse(value) : null
    return parsed && typeof parsed === 'object' ? parsed : {}
  } catch (_) {
    return {}
  }
}

const cookieOptionsFor = (value, now) => {
  const { expiresAt } = parsePromoCookie(value)
  const maxage = Number(expiresAt) - now
  return maxage > 0 ? { ...PROMO_COOKIE_OPTIONS, maxage } : PROMO_COOKIE_OPTIONS
}

export const getGlobalPromoCookie = async (dispatch, now = Date.now()) => {
  const value = await dispatch(cookie(GLOBAL_PROMO_COOKIE_KEY))
  // Re-home whatever was read at path=/ (it may be a legacy
  // /subscriptions copy) so later full loads of other pages find it too
  if (value !== undefined) {
    await expireLegacyPromoCookie(dispatch)
    await dispatch(
      cookie(GLOBAL_PROMO_COOKIE_KEY, value, cookieOptionsFor(value, now))
    )
  }
  return { value }
}

export const setGlobalPromoCookie = async (
  dispatch,
  value,
  now = Date.now()
) => {
  await expireLegacyPromoCookie(dispatch)
  await dispatch(
    cookie(GLOBAL_PROMO_COOKIE_KEY, value, cookieOptionsFor(value, now))
  )
}

// How long a validated code is kept: until the promotion it belongs to ends
// (when it is the active promotion's code and that end is still ahead),
// capped at 14 days.
export const promoCookieMaxAge = (codename, promotion, now = Date.now()) => {
  const isActiveCode =
    Boolean(codename) && promotion?.couponPromoCode?.codename === codename
  const untilEnd = isActiveCode ? Date.parse(promotion.endingDate) - now : NaN
  return untilEnd > 0
    ? Math.min(PROMO_COOKIE_MAX_AGE_MS, untilEnd)
    : PROMO_COOKIE_MAX_AGE_MS
}

export const storePromoCode = (
  dispatch,
  codename,
  promotion,
  now = Date.now()
) =>
  setGlobalPromoCookie(
    dispatch,
    JSON.stringify({
      codename,
      expiresAt: now + promoCookieMaxAge(codename, promotion, now)
    }),
    now
  )

// SPEC-092: the code of the sitewide promotion running right now, or ''.
// The promotion endpoint already answers with the current one only; the
// dates are checked again so a long-lived tab stops at the end.
export const activePromotionCode = (promotion, now = Date.now()) => {
  const codename = promotion?.couponPromoCode?.codename
  if (!codename) return ''
  const start = Date.parse(promotion.startingDate)
  const end = Date.parse(promotion.endingDate)
  if (Number.isFinite(start) && start > now) return ''
  if (Number.isFinite(end) && end <= now) return ''
  return codename
}

// The server's per-plan promo entry carries discount_amount 0 wherever
// checkout would refuse the code — plan not discountable and not scoped at it
// (SPEC-062) — so this is the one place "does the promo apply here" is
// answered, with no plan id or name involved (SPEC-063).
export const promoDiscountForPlan = (globalPromo, plan) => {
  const entry = plan && globalPromo?.plans?.[plan.id]
  const amount = parseFloat(entry?.discountAmount)
  return Number.isFinite(amount) && amount > 0 ? amount : 0
}

// SPEC-093: the checkout's copy of the sitewide promo for one plan, carrying
// the endpoint's own money values: discount = discount_amount and
// priceDiscount = the price the subscribe is charged. Nothing is derived
// here, so the price shown is the price charged, to the cent.
export const promoCodeForPlan = (globalPromo, plan) => {
  const entry = plan && globalPromo?.plans?.[plan.id]
  return {
    ...globalPromo,
    discount: promoDiscountForPlan(globalPromo, plan),
    priceDiscount: entry?.priceDiscount,
    // the plan these amounts belong to; never priced against another
    planId: plan?.id
  }
}
